/**
 * ContentItemAccordion Component
 *
 * Accordion for guide content items - used inside SortableList.Item.
 * The drag handle is provided by SortableList.Item, this component
 * only renders the accordion content.
 *
 * Features:
 * - Smart accordion with content preview
 * - Field grouping for better organization
 * - Delete action in accordion summary
 * - Theme-aware styling with micro-interactions
 * - Performance optimized with React.memo
 */

import React from 'react';
import {
  Box,
  Stack,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Tooltip,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import {
  StyledTextField,
  StyledTextArea,
  MediaUploadField,
} from '@modules/ui-components';
import type { ContentItem } from '../types';

export interface ContentItemAccordionProps {
  item: ContentItem;
  index: number;
  onFieldChange: (index: number, field: keyof ContentItem, value: string) => void;
  onMediaChange: (index: number, field: 'image' | 'video', value: string | File | null) => void;
  onRemove: (index: number) => void;
}

/**
 * ContentItemAccordion - Accordion content for guide items
 * Memoized to prevent unnecessary re-renders
 */
export const ContentItemAccordion = React.memo<ContentItemAccordionProps>(
  ({ item, index, onFieldChange, onMediaChange, onRemove }) => {
    const theme = useTheme();

    // ============================================================
    // Content Analysis for Smart Preview
    // ============================================================
    const filledFieldsCount = [
      item.title,
      item.description,
      item.subtitle,
      item.fullArticleLink,
      item.image,
      item.video,
    ].filter(Boolean).length;

    const hasImage = Boolean(item.image);
    const hasVideo = Boolean(item.video);

    // Truncate subtitle for preview
    const previewSubtitle = item.subtitle
      ? item.subtitle.length > 50
        ? `${item.subtitle.substring(0, 50)}...`
        : item.subtitle
      : '';

    // Generate accordion summary text
    const summaryText = item.title || `Content Item ${index + 1}`;

    // ============================================================
    // Render
    // ============================================================
    return (
      <Accordion
        defaultExpanded={false}
        sx={{
          backgroundColor: 'background.paper',
          boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
          transition: theme.transitions.create(
            ['box-shadow'],
            {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }
          ),
          '&:before': {
            display: 'none',
          },
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
          },
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px !important`,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* ============================================================ */}
        {/* Accordion Summary - Title, Preview, Delete */}
        {/* ============================================================ */}
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              my: 0,
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
            },
            transition: theme.transitions.create(['background-color'], {
              duration: theme.transitions.duration.shortest,
            }),
          }}
        >
          {/* Title and Preview Section */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {summaryText}
              </Typography>

              {/* Field Count Badge */}
              <Chip
                label={`${filledFieldsCount}/6`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />

              {/* Media Badges */}
              {hasImage && (
                <Tooltip title="Has image" placement="top">
                  <ImageIcon
                    fontSize="small"
                    sx={{
                      color: theme.palette.success.main,
                      fontSize: '1.1rem',
                    }}
                  />
                </Tooltip>
              )}
              {hasVideo && (
                <Tooltip title="Has video" placement="top">
                  <VideoIcon
                    fontSize="small"
                    sx={{
                      color: theme.palette.info.main,
                      fontSize: '1.1rem',
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Subtitle Preview */}
            {previewSubtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: 0.25,
                }}
              >
                {previewSubtitle}
              </Typography>
            )}
          </Box>

          {/* Delete Button */}
          <Tooltip title="Delete item" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.error.main,
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
                transition: theme.transitions.create(['color', 'background-color'], {
                  duration: theme.transitions.duration.shortest,
                }),
                p: 0.75,
              }}
              aria-label="Delete content item"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AccordionSummary>

        {/* ============================================================ */}
        {/* Accordion Details - Form Fields */}
        {/* ============================================================ */}
        <AccordionDetails
          sx={{
            pt: 2,
            pb: 3,
            px: 3,
            backgroundColor: alpha(theme.palette.background.default, 0.3),
          }}
        >
          <Stack spacing={3}>
            {/* ============================================================ */}
            {/* Text Content Section */}
            {/* ============================================================ */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 1.5,
                  display: 'block',
                }}
              >
                Text Content
              </Typography>

              <Stack spacing={2}>
                <StyledTextField
                  label="Title"
                  value={item.title || ''}
                  onChange={(value) => onFieldChange(index, 'title', value)}
                  fullWidth
                  placeholder="Enter a catchy title..."
                  helperText={
                    item.title
                      ? `${item.title.length} characters`
                      : 'Main heading for this content item'
                  }
                />

                <StyledTextField
                  label="Subtitle"
                  value={item.subtitle || ''}
                  onChange={(value) => onFieldChange(index, 'subtitle', value)}
                  fullWidth
                  placeholder="Optional subtitle..."
                  helperText="Supporting text below the title (optional)"
                />

                <StyledTextArea
                  label="Description"
                  value={item.description || ''}
                  onChange={(value) => onFieldChange(index, 'description', value)}
                  fullWidth
                  rows={3}
                  placeholder="Describe this content item..."
                  helperText={
                    item.description
                      ? `${item.description.length} characters`
                      : 'Detailed description of the content (optional)'
                  }
                />

                <StyledTextField
                  label="Full Article Link"
                  value={item.fullArticleLink || ''}
                  onChange={(value) => onFieldChange(index, 'fullArticleLink', value)}
                  fullWidth
                  placeholder="https://example.com/article"
                  helperText="Link to full article or resource (optional)"
                />
              </Stack>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* ============================================================ */}
            {/* Media Section */}
            {/* ============================================================ */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 1.5,
                  display: 'block',
                }}
              >
                Media
              </Typography>

              <Stack spacing={2}>
                <MediaUploadField
                  label="Image"
                  value={item.image || null}
                  onChange={(value) => onMediaChange(index, 'image', value)}
                  mediaType="image"
                  showPreview={true}
                  fullWidth
                  helperText="Upload an image or provide a URL (optional)"
                />

                <MediaUploadField
                  label="Video"
                  value={item.video || null}
                  onChange={(value) => onMediaChange(index, 'video', value)}
                  mediaType="video"
                  showPreview={true}
                  fullWidth
                  helperText="Upload a video or provide a URL (optional)"
                />
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  },
  // Custom comparison function - only re-render if item data or index changed
  (prevProps, nextProps) => {
    return (
      prevProps.item === nextProps.item &&
      prevProps.index === nextProps.index
    );
  }
);

// Display name for debugging
ContentItemAccordion.displayName = 'ContentItemAccordion';
